import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react'
import { gsap } from 'gsap'
import styles from './ReadingFlowView.module.scss'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import recognitionLangs from '../speech/recognitionLangs'
import languages from '../languages'
import { celebrate } from './juice'
import {
  tokenizeLine, tokenizeTranscript, tokenMatches, romanizationOf, cancelTts,
} from '../speech/flowMatch'

import CloseIcon from '@mui/icons-material/Close'
import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import ReplayIcon from '@mui/icons-material/Replay'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

// Practice the non-English side of each card, like the handwriting view.
const practiceSideOf = (card) => {
  const t = card.translations?.[0]
  if (card.sourceLanguage && card.sourceLanguage !== 'eng' && card.source) {
    return { text: card.source, lang: card.sourceLanguage, translation: t?.target || '' }
  }
  if (t?.target && t.targetLanguage !== 'eng') {
    return { text: t.target, lang: t.targetLanguage, translation: card.source || '' }
  }
  return { text: card.source, lang: card.sourceLanguage || 'eng', translation: t?.target || '' }
}

export default function ReadingFlowView({ cards, voiceSpeed, onClose }) {
  const lines = useMemo(
    () => (cards || []).map(practiceSideOf).filter(i => i.text && i.text.trim()),
    [cards]
  )

  const [idx, setIdx] = useState(0)
  // Word states are tagged with the line index they belong to. After advancing,
  // the previous line's (all-hit) states linger in React state for one render —
  // without the tag, a same-word-count next line would read them as an instant
  // win and auto-complete with confetti before the user says anything.
  const [wordStates, setWordStates] = useState({ forIdx: -1, states: [] }) // states: 'pending' | 'hit' | 'miss'
  const [listening, setListening] = useState(false)
  const [interim, setInterim] = useState('')
  const [showRoman, setShowRoman] = useState(true)
  const [showTranslation, setShowTranslation] = useState(true)
  const [micError, setMicError] = useState(null)
  // Real recognizer audio phase, so the status line can tell "mic merely open"
  // apart from "actual sound arriving". 'off' | 'open' | 'sound' | 'speech'
  const [micPhase, setMicPhase] = useState('off')

  const viewportRef = useRef(null)
  const trackRef = useRef(null)
  const lineRefs = useRef([])
  const recRef = useRef(null)
  const listeningRef = useRef(false)
  const completedRef = useRef(false)
  const advanceTimerRef = useRef(null)
  // Bumped every time recognition is (re)started or torn down. Handlers on a
  // superseded recognizer instance check this and go inert — otherwise an
  // aborted recognizer's onend can restart itself and keep matching against
  // the previous line's words while the new instance fails to start.
  const sessionRef = useRef(0)
  // Consecutive sessions that ended without ever capturing audio. When this
  // climbs, the keep-alive loop is just resurrecting a dead mic — stop and
  // tell the user instead of pulsing forever.
  const deadSessionsRef = useRef(0)

  // Pronunciation coaching: after N failed tries on a line we ask the Mac Studio
  // to explain, in English, what the recognizer is hearing and how to say the
  // word so it matches. A "try" = a new final transcript that didn't complete
  // the line (deduped by text so cumulative/repeat finals don't over-count).
  const FAIL_THRESHOLD = 5
  const attemptsRef = useRef(0)
  const lastFinalRef = useRef('')     // last final transcript we counted
  const coachedLineRef = useRef(null) // line text we've already coached, so it fires once
  const [coach, setCoach] = useState(null) // { loading, heard, tip, error } | null
  const serverUrl = process.env.REACT_APP_OLLAMA_URI
  const apiKey = process.env.REACT_APP_OLLAMA_KEY

  const requestCoaching = useCallback(async (target, lang, heard) => {
    setCoach({ loading: true, heard, tip: '' })
    if (!serverUrl) { setCoach({ loading: false, heard, tip: '', error: true }); return }
    const langName = languages[lang] || lang
    const prompt = `A learner is practising speaking ${langName} out loud using their phone's speech-to-text.
They are trying to say this exact line: "${target}".
But the speech recogniser keeps hearing them as: "${heard || '(nothing clear)'}".

In 2-3 short sentences of plain, friendly English, help them:
1. Say what the mismatch suggests they are mis-saying (which sound or syllable).
2. Give ONE concrete tip to pronounce it so the recogniser matches — e.g. an English-sound approximation, which syllable to stress, or to slow down.
Be encouraging. Do NOT write in ${langName} except to quote a specific sound. Respond with ONLY the advice.`
    try {
      const res = await fetch(`${serverUrl}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        body: JSON.stringify({ model: 'qwen2.5:7b', prompt, stream: false, options: { temperature: 0.4, num_predict: 170 } }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setCoach({ loading: false, heard, tip: (data.response || '').trim() })
    } catch (e) {
      setCoach({ loading: false, heard, tip: '', error: true })
    }
  }, [serverUrl, apiKey])

  const line = lines[idx]
  const words = useMemo(() => tokenizeLine(line?.text, line?.lang), [line])
  const states = wordStates.forIdx === idx ? wordStates.states : []

  // Reset word states whenever the line changes.
  useEffect(() => {
    setWordStates({ forIdx: idx, states: Array(words.length).fill('pending') })
    setInterim('')
    completedRef.current = false
    attemptsRef.current = 0
    lastFinalRef.current = ''
    coachedLineRef.current = null
    setCoach(null)
  }, [idx, words.length])

  // Spotify-style scroll: glide the track so the current line sits centered.
  useLayoutEffect(() => {
    const el = lineRefs.current[idx]
    const viewport = viewportRef.current
    if (!el || !viewport || !trackRef.current) return
    const y = viewport.clientHeight / 2 - el.offsetTop - el.offsetHeight / 2
    gsap.to(trackRef.current, { y, duration: 0.8, ease: 'power3.out' })
  }, [idx, lines])

  const stopListening = useCallback(() => {
    listeningRef.current = false
    setListening(false)
    setMicPhase('off')
    sessionRef.current++
    try { recRef.current?.stop() } catch { /* already stopped */ }
  }, [])

  const advance = useCallback((dir = 1) => {
    clearTimeout(advanceTimerRef.current)
    setIdx(prev => {
      const next = prev + dir
      if (next < 0 || next >= lines.length) return prev
      return next
    })
  }, [lines.length])

  // All words hit → celebrate the line, then flow to the next one.
  useEffect(() => {
    if (wordStates.forIdx !== idx) return
    if (words.length === 0 || wordStates.states.length !== words.length) return
    if (!wordStates.states.every(s => s === 'hit') || completedRef.current) return
    completedRef.current = true
    // Solved it — clear any coaching and the failure count.
    attemptsRef.current = 0
    setCoach(null)
    const el = lineRefs.current[idx]
    if (el) {
      const rect = el.getBoundingClientRect()
      celebrate(rect.left + rect.width / 2, rect.top + rect.height / 2, { count: 30, spread: 170 })
      gsap.fromTo(el, { scale: 1 }, { scale: 1.06, duration: 0.18, yoyo: true, repeat: 1, ease: 'power2.out', clearProps: 'scale' })
    }
    advanceTimerRef.current = setTimeout(() => advance(1), 1200)
  }, [wordStates, words.length, idx, advance])

  // Merge a (cumulative) recognition transcript into the word states.
  // Hits are sticky so a recognizer restart can't un-hit words.
  const applyTranscript = useCallback((transcript, isFinal) => {
    if (!line) return
    const tokens = tokenizeTranscript(transcript, line.lang)
    setWordStates(prev => {
      if (prev.forIdx !== idx) return prev
      const next = [...prev.states]
      let wi = 0
      for (const tok of tokens) {
        // Look a few words ahead so one flubbed word doesn't stall the line.
        for (let j = wi; j < Math.min(next.length, wi + 4); j++) {
          if (next[j] === 'hit') { if (j === wi) wi++; continue }
          if (tokenMatches(tok, words[j]?.norm, line.lang)) {
            next[j] = 'hit'
            wi = j + 1
            break
          }
        }
      }
      if (isFinal) {
        const lastHit = next.lastIndexOf('hit')
        for (let k = 0; k < lastHit; k++) {
          if (next[k] === 'pending') next[k] = 'miss'
        }
      }
      return { forIdx: prev.forIdx, states: next }
    })
  }, [line, words, idx])

  const startListening = useCallback(() => {
    if (!SpeechRecognition) {
      setMicError('Speech recognition is not supported in this browser.')
      return
    }
    if (!line) return
    setMicError(null)
    cancelTts()
    const session = ++sessionRef.current
    const rec = new SpeechRecognition()
    rec.lang = recognitionLangs[line.lang] || 'en-US'
    rec.continuous = true
    rec.interimResults = true
    // Per-session flags. `heardSound` is the load-bearing one: Chrome fires
    // onaudiostart just for opening the mic, but onsoundstart only when actual
    // audio energy arrives. If sessions keep opening yet never hear sound, the
    // selected input device is silent — no amount of restarting will help.
    let heardSound = false

    const stopWithError = (msg) => {
      listeningRef.current = false
      setListening(false)
      setMicPhase('off')
      setMicError(msg)
    }

    rec.onaudiostart = () => {
      if (session !== sessionRef.current) return
      setMicPhase('open')
    }
    rec.onsoundstart = () => {
      if (session !== sessionRef.current) return
      heardSound = true
      deadSessionsRef.current = 0
      setMicPhase('sound')
    }
    rec.onspeechstart = () => {
      if (session !== sessionRef.current) return
      heardSound = true
      deadSessionsRef.current = 0
      setMicPhase('speech')
    }
    rec.onresult = (event) => {
      if (session !== sessionRef.current) return
      heardSound = true
      deadSessionsRef.current = 0
      setMicPhase('speech')
      let transcript = ''
      let finalText = ''
      let sawFinal = false
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript + ' '
        if (event.results[i].isFinal) { finalText += event.results[i][0].transcript + ' '; sawFinal = true }
      }
      setInterim(transcript.trim().slice(-70))
      applyTranscript(transcript, sawFinal)

      // Count a failed try: a NEW final transcript that hasn't solved the line.
      if (sawFinal && !completedRef.current) {
        const heard = finalText.trim()
        if (heard && heard !== lastFinalRef.current) {
          lastFinalRef.current = heard
          attemptsRef.current += 1
          if (attemptsRef.current >= FAIL_THRESHOLD && coachedLineRef.current !== line.text) {
            coachedLineRef.current = line.text
            requestCoaching(line.text, line.lang, heard)
          }
        }
      }
    }
    rec.onerror = (e) => {
      if (session !== sessionRef.current) return
      console.warn('[flow] recognition error:', e.error, 'lang:', rec.lang)
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        stopWithError('Microphone access was blocked — allow it in browser settings.')
      } else if (e.error === 'audio-capture') {
        stopWithError('No audio device captured — pick the right microphone in the browser/site settings.')
      } else if (e.error === 'language-not-supported') {
        stopWithError(`This browser can't recognize ${rec.lang} speech.`)
      } else if (e.error === 'network') {
        setMicError('Speech service network error — retrying…')
      }
      // 'no-speech' and 'aborted' are routine; the onend keep-alive handles them.
    }
    // The recognizer times out on silence — keep it alive while the mic is on.
    rec.onend = () => {
      if (session !== sessionRef.current) return
      if (!listeningRef.current) {
        setMicPhase('off')
        return
      }
      if (completedRef.current) return
      // Session opened but never heard a single sound — count it. Too many in a
      // row means the input device is silent, so stop the pointless flicker.
      if (!heardSound) {
        deadSessionsRef.current++
        if (deadSessionsRef.current >= 4) {
          console.warn('[flow] giving up: input device is silent across 4 sessions')
          stopWithError('Mic is open but no sound is reaching it. Chrome is likely using the wrong/silent input — click the 🔒/camera icon in the address bar, pick your real microphone, then tap the mic again.')
          return
        }
      }
      // Longer gap between restarts so the status doesn't strobe, and Chrome's
      // service gets a beat to recover.
      setTimeout(() => {
        if (session !== sessionRef.current || !listeningRef.current) return
        try { rec.start() } catch { /* restart raced with a manual start */ }
      }, 500)
    }

    recRef.current = rec
    listeningRef.current = true
    deadSessionsRef.current = 0
    setListening(true)
    try {
      rec.start()
    } catch {
      // The previous session may not have fully released yet — retry once.
      setTimeout(() => {
        if (session !== sessionRef.current || !listeningRef.current) return
        try { rec.start() } catch { /* give up; user can toggle the mic */ }
      }, 400)
    }
  }, [line, applyTranscript, requestCoaching])

  // When the line changes while the mic is on, restart recognition with a
  // fresh session (clears the cumulative transcript, picks up the new lang).
  useEffect(() => {
    if (!listeningRef.current) return
    sessionRef.current++
    try { recRef.current?.abort() } catch { /* fine */ }
    const t = setTimeout(() => { if (listeningRef.current) startListening() }, 350)
    return () => clearTimeout(t)
  }, [idx]) // eslint-disable-line

  useEffect(() => () => {
    clearTimeout(advanceTimerRef.current)
    listeningRef.current = false
    sessionRef.current++
    try { recRef.current?.abort() } catch { /* fine */ }
  }, [])

  const retryLine = () => {
    clearTimeout(advanceTimerRef.current)
    completedRef.current = false
    attemptsRef.current = 0
    lastFinalRef.current = ''
    setWordStates({ forIdx: idx, states: Array(words.length).fill('pending') })
    setInterim('')
    if (listeningRef.current) {
      sessionRef.current++
      try { recRef.current?.abort() } catch { /* fine */ }
      setTimeout(startListening, 250)
    }
  }

  const speakLine = () => {
    if (!line) return
    speak(line.text, voices[line.lang] || voices['eng'], voiceSpeed)
  }

  const handleWordClick = (i) => {
    if (!line) return
    speak(words[i].raw, voices[line.lang] || voices['eng'], voiceSpeed)
    // Give the word another chance after hearing it.
    setWordStates(prev => (prev.forIdx !== idx ? prev : {
      forIdx: prev.forIdx,
      states: prev.states.map((s, j) => (j === i && s === 'miss' ? 'pending' : s)),
    }))
  }

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') advance(1)
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') advance(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [advance])

  if (lines.length === 0) {
    return (
      <div className={styles.view}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.empty}>No sentences to read.</div>
      </div>
    )
  }

  const roman = romanizationOf(line?.text, line?.lang)
  const recLangLabel = recognitionLangs[line?.lang] || 'en-US'

  return (
    <div className={styles.view}>
      <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>

      <div className={styles.topBar}>
        <span className={styles.modeLabel}>Flow · {languages[line?.lang] || line?.lang}</span>
        <label className={styles.toggle}>
          <input type="checkbox" checked={showRoman} onChange={e => setShowRoman(e.target.checked)} />
          romanization
        </label>
        <label className={styles.toggle}>
          <input type="checkbox" checked={showTranslation} onChange={e => setShowTranslation(e.target.checked)} />
          translation
        </label>
      </div>

      <div className={styles.viewport} ref={viewportRef}>
        <div className={styles.fadeTop} />
        <div className={styles.fadeBottom} />
        <div className={styles.track} ref={trackRef}>
          {lines.map((l, i) => {
            const isCurrent = i === idx
            const cls = [
              styles.line,
              isCurrent ? styles.lineCurrent : (i < idx ? styles.linePast : styles.lineFuture),
            ].join(' ')
            return (
              <div
                key={i}
                ref={el => { lineRefs.current[i] = el }}
                className={cls}
                onClick={() => { if (!isCurrent) setIdx(i) }}
              >
                {isCurrent ? (
                  <>
                    <div className={styles.lineWords}>
                      {words.map((w, wi) => (
                        <span
                          key={wi}
                          className={[
                            styles.word,
                            states[wi] === 'hit' ? styles.wordHit : '',
                            states[wi] === 'miss' ? styles.wordMiss : '',
                          ].join(' ')}
                          onClick={(e) => { e.stopPropagation(); handleWordClick(wi) }}
                        >
                          {w.raw}
                        </span>
                      ))}
                    </div>
                    {showRoman && roman && <div className={styles.lineRoman}>{roman}</div>}
                    {showTranslation && l.translation && <div className={styles.lineTranslation}>{l.translation}</div>}
                  </>
                ) : (
                  l.text
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className={styles.bottomBar}>
        <div className={styles.interim}>
          {micError
            ? micError
            : listening
              ? (interim || (
                  micPhase === 'speech' ? `hearing you… (${recLangLabel})`
                    : micPhase === 'sound' ? `picking up sound… (${recLangLabel})`
                    : `mic open (${recLangLabel}) — say the line, no sound detected yet`
                ))
              : ''}
        </div>
        <div className={styles.controls}>
          <button className={styles.sideBtn} onClick={() => advance(-1)} disabled={idx === 0} title="Previous line">
            <KeyboardArrowUpIcon />
          </button>
          <button className={styles.sideBtn} onClick={speakLine} title="Hear the line">
            <VolumeUpIcon />
          </button>
          <button
            className={`${styles.micBtn} ${listening ? styles.micBtnActive : ''}`}
            onClick={() => (listening ? stopListening() : startListening())}
            title={listening ? 'Stop the mic' : 'Read the line aloud'}
          >
            {listening ? <MicIcon style={{ fontSize: 34 }} /> : <MicOffIcon style={{ fontSize: 34 }} />}
          </button>
          <button className={styles.sideBtn} onClick={retryLine} title="Retry this line">
            <ReplayIcon />
          </button>
          <button className={styles.sideBtn} onClick={() => advance(1)} disabled={idx === lines.length - 1} title="Next line">
            <KeyboardArrowDownIcon />
          </button>
        </div>
        <div className={styles.progress}>{idx + 1} / {lines.length}</div>
      </div>

      {coach && (
        <div className={styles.coachPanel}>
          <div className={styles.coachHeader}>
            <span className={styles.coachTitle}>💬 Pronunciation help</span>
            <button className={styles.coachClose} onClick={() => setCoach(null)} title="Dismiss">
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
          </div>

          <div className={styles.coachRow}>
            <span className={styles.coachLabel}>you're saying</span>
            <span className={styles.coachTarget}>{line.text}</span>
          </div>
          {roman && <div className={styles.coachRoman}>{roman}</div>}
          <div className={styles.coachRow}>
            <span className={styles.coachLabel}>mic hears</span>
            <span className={styles.coachHeard}>{coach.heard || '—'}</span>
          </div>

          <div className={styles.coachTip}>
            {coach.loading
              ? <span className={styles.coachDots}><span /><span /><span /></span>
              : coach.error
                ? "Couldn't reach the Mac Studio for a tip — try saying it slower, one syllable at a time."
                : coach.tip}
          </div>

          <div className={styles.coachActions}>
            <button className={styles.coachBtn} onClick={speakLine}>
              <VolumeUpIcon style={{ fontSize: 16 }} /> Hear it
            </button>
            <button className={`${styles.coachBtn} ${styles.coachBtnPrimary}`} onClick={() => { setCoach(null); retryLine() }}>
              Try again
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
